import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { withPrefix } from 'gatsby'

import { STORAGE_KEY, emptyData, load, reset, save } from '../lib/personal'

// Device-only personalization state, read from localStorage after hydration.
//
// RULE: any component that shows personal data must return null until `ready`
// is true. Static HTML is identical for every visitor; `ready` only flips in
// useEffect after the first client read, so rendering personal UI before then
// would cause a hydration mismatch.
//
// `available` turns false when the browser refuses a write (private mode, full
// storage). Treat that as "personalization off" — never as an error for a child.

const noop = () => {}

const PersonalContext = createContext({
  ready: false,
  available: false,
  data: emptyData(),
  activeChild: null,
  setActiveChild: noop,
  addChild: noop,
  updateChild: noop,
  removeChild: noop,
  setProgress: noop,
  markCompleted: noop,
  toggleFavourite: noop,
  resetAll: noop,
  storyIndex: null,
  loadStoryIndex: () => Promise.resolve([]),
})

const newId = () =>
  Date.now().toString(36) + Math.random().toString(36).slice(2, 8)

// public/story-index.json, written by gatsby-node.js: [{ slug, title, title_hi,
// book, bookSlug, order, ageBands, featuredImage, hasHindiAudio, durationSec }].
// `order` is the chapter position as text (compare with collectionOrder.js). Fetched once
// per page load, and only when a component asks for it. A failed fetch yields
// [] so callers simply show nothing.
let storyIndexRequest = null
const fetchStoryIndex = () => {
  if (!storyIndexRequest) {
    storyIndexRequest = fetch(withPrefix('/story-index.json'))
      .then(res => (res.ok ? res.json() : []))
      .then(list => (Array.isArray(list) ? list : []))
      .catch(() => [])
  }
  return storyIndexRequest
}

const withoutSlug = (list, slug) => (list || []).filter(s => s !== slug)

export const PersonalProvider = ({ children }) => {
  const [data, setData] = useState(emptyData)
  const [ready, setReady] = useState(false)
  const [available, setAvailable] = useState(true)
  const [storyIndex, setStoryIndex] = useState(null)
  const dataRef = useRef(data)

  useEffect(() => {
    const read = () => {
      dataRef.current = load()
      setData(dataRef.current)
    }
    read()
    setReady(true)

    // Keep tabs in step when another tab on this device changes the data.
    const onStorage = event => {
      if (event.key === STORAGE_KEY || event.key === null) read()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Apply `update(current)` and persist it. On a failed write the in-memory
  // state is left as it was and personalization is switched off.
  const commit = useCallback(update => {
    const saved = save(update(dataRef.current))
    if (!saved) {
      setAvailable(false)
      return null
    }
    dataRef.current = saved
    setData(saved)
    return saved
  }, [])

  const setActiveChild = useCallback(
    id => commit(d => ({ ...d, activeChildId: id })),
    [commit]
  )

  // Returns the new child's id, or null when the device cannot save.
  const addChild = useCallback(
    ({ name, ageBand, lang }) => {
      const id = newId()
      const saved = commit(d => ({
        ...d,
        children: d.children.concat({ id, name, ageBand, lang }),
        activeChildId: d.activeChildId || id,
      }))
      return saved ? id : null
    },
    [commit]
  )

  const updateChild = useCallback(
    (id, patch) =>
      commit(d => ({
        ...d,
        children: d.children.map(c => (c.id === id ? { ...c, ...patch, id } : c)),
      })),
    [commit]
  )

  // Removing a child also drops their progress, completed and favourites
  // (the store prunes maps for children that no longer exist).
  const removeChild = useCallback(
    id =>
      commit(d => ({
        ...d,
        children: d.children.filter(c => c.id !== id),
        activeChildId: d.activeChildId === id ? null : d.activeChildId,
      })),
    [commit]
  )

  // Merge `patch` ({ storySlug, paraIndex, audioSec, lang }) into the active
  // child's entry for `bookSlug`. Stories outside a book key by their own slug.
  const setProgress = useCallback(
    (bookSlug, patch) =>
      commit(d => {
        const childId = d.activeChildId
        if (!childId || !bookSlug) return d
        const books = d.progress[childId] || {}
        return {
          ...d,
          progress: {
            ...d.progress,
            [childId]: {
              ...books,
              [bookSlug]: { ...books[bookSlug], ...patch, updatedAt: Date.now() },
            },
          },
        }
      }),
    [commit]
  )

  // Completed slugs are kept in completion order; re-completing moves a story
  // to the end so the oldest entries are the ones trimmed first.
  const markCompleted = useCallback(
    storySlug =>
      commit(d => {
        const childId = d.activeChildId
        if (!childId || !storySlug) return d
        return {
          ...d,
          completed: {
            ...d.completed,
            [childId]: withoutSlug(d.completed[childId], storySlug).concat(storySlug),
          },
        }
      }),
    [commit]
  )

  const toggleFavourite = useCallback(
    storySlug =>
      commit(d => {
        const childId = d.activeChildId
        if (!childId || !storySlug) return d
        const list = d.favourites[childId] || []
        return {
          ...d,
          favourites: {
            ...d.favourites,
            [childId]:
              list.indexOf(storySlug) === -1
                ? list.concat(storySlug)
                : withoutSlug(list, storySlug),
          },
        }
      }),
    [commit]
  )

  // Clears the device whether or not the browser lets us remove the key.
  const resetAll = useCallback(() => {
    reset()
    dataRef.current = emptyData()
    setData(dataRef.current)
  }, [])

  // Call from an effect in any component that needs the index; `storyIndex`
  // stays null until it has loaded.
  const loadStoryIndex = useCallback(
    () =>
      fetchStoryIndex().then(list => {
        setStoryIndex(list)
        return list
      }),
    []
  )

  const value = useMemo(() => {
    const activeChild =
      data.children.find(c => c.id === data.activeChildId) || null
    return {
      ready,
      available,
      data,
      activeChild,
      setActiveChild,
      addChild,
      updateChild,
      removeChild,
      setProgress,
      markCompleted,
      toggleFavourite,
      resetAll,
      storyIndex,
      loadStoryIndex,
    }
  }, [
    ready,
    available,
    data,
    setActiveChild,
    addChild,
    updateChild,
    removeChild,
    setProgress,
    markCompleted,
    toggleFavourite,
    resetAll,
    storyIndex,
    loadStoryIndex,
  ])

  return (
    <PersonalContext.Provider value={value}>{children}</PersonalContext.Provider>
  )
}

export const usePersonal = () => useContext(PersonalContext)
